import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useZunelo } from '../../context/ZuneloContext';
import { SearchService } from '../../services/searchService';
import { Modal, BrandIcon } from '../../ui/common';

export const SearchModal: React.FC = () => {
  const {
    searchTarget,
    setSearchTarget,
    items,
    agents,
    addCustomDailyLine,
    addReminder,
    addNote,
    navigateToItem,
  } = useZunelo();

  const [query, setQuery] = useState<string>('');
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  const isOpen = searchTarget !== null;

  const results = useMemo(() => {
    const res = SearchService.searchItems(query, items, agents);
    const combined = Array.isArray(res) ? res : [...(res.items || []), ...(res.agents || [])];
    return combined.slice(0, 8);
  }, [query, items, agents]);

  // Ensure activeIndex is always valid within results range
  const safeActiveIndex = results.length === 0 ? 0 : Math.min(activeIndex, results.length - 1);

  // Auto-focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Scroll active item into view when active index changes
  useEffect(() => {
    if (!resultsContainerRef.current) return;
    const activeElem = resultsContainerRef.current.querySelector<HTMLElement>(`[data-index="${safeActiveIndex}"]`);
    if (activeElem && typeof activeElem.scrollIntoView === 'function') {
      activeElem.scrollIntoView({ block: 'nearest' });
    }
  }, [safeActiveIndex]);

  if (!isOpen) return null;

  const handleClose = () => {
    setSearchTarget(null);
    setQuery('');
    setActiveIndex(0);
  };

  const handleSelect = (itemId: string) => {
    if (!searchTarget) return;

    const parts = searchTarget.split('|');
    const kind = parts[0];

    if (kind === 'daily') {
      const groupId = parts[1] as 'ayer' | 'hoy' | 'bloq';
      const item = items.find((i) => i.id === itemId);
      if (item) {
        addCustomDailyLine(groupId, item.title);
      }
    } else if (kind === 'reminder') {
      const item = items.find((i) => i.id === itemId);
      if (item) {
        addReminder(item.title, undefined, undefined, item.id);
      }
    } else if (kind === 'note') {
      const item = items.find((i) => i.id === itemId);
      if (item) {
        addNote(item.title, item.id);
      }
    } else {
      // Direct navigation
      const item = items.find((i) => i.id === itemId);
      const agent = agents.find((a) => a.name === itemId);
      if (item) {
        navigateToItem(item.source, item.id);
      } else if (agent) {
        navigateToItem('agents', agent.name);
      }
    }

    handleClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (results.length > 0) {
        setActiveIndex((prev) => (prev + 1) % results.length);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (results.length > 0) {
        setActiveIndex((prev) => (prev - 1 + results.length) % results.length);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results.length > 0 && safeActiveIndex >= 0 && safeActiveIndex < results.length) {
        const selected = results[safeActiveIndex];
        const isAgent = 'name' in selected;
        const id = isAgent ? selected.name : selected.id;
        handleSelect(id);
      }
    }
  };

  const kbdStyle: React.CSSProperties = {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 10,
    fontWeight: 600,
    padding: '1.5px 5px',
    borderRadius: 4,
    background: 'var(--z-fill-2)',
    border: 'none',
    color: 'var(--z-muted-2)',
    marginRight: 4,
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width={500}>
      <div
        style={{ display: 'flex', flexDirection: 'column' }}
        onKeyDown={handleKeyDown}
      >
        {/* Search Input Bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 16px',
            borderBottom: 'none',
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            style={{ color: 'var(--z-dim)' }}
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            ref={inputRef}
            autoFocus
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            placeholder="Buscar ticket, PR, hilo o agente…"
            style={{
              flex: '1 1 auto',
              border: 'none',
              background: 'transparent',
              fontFamily: 'inherit',
              fontSize: 14,
              color: 'var(--z-text)',
            }}
            aria-label="Buscar ticket, PR, hilo o agente"
          />
          {query && (
            <button
              onClick={() => {
                setQuery('');
                setActiveIndex(0);
              }}
              style={{ color: 'var(--z-dim)', fontSize: 13, cursor: 'pointer', padding: '2px 4px' }}
              aria-label="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>

        {/* Results List */}
        <div
          ref={resultsContainerRef}
          role="listbox"
          aria-label="Resultados de búsqueda"
          style={{
            maxHeight: '50vh',
            overflowY: 'auto',
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {results.length === 0 ? (
            <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--z-dim)', fontSize: 13 }}>
              No se encontraron resultados para "{query}"
            </div>
          ) : (
            results.map((r, idx) => {
              const isAgent = 'name' in r;
              const id = isAgent ? r.name : r.id;
              const key = isAgent ? r.name : (r.ticket || r.primary);
              const title = isAgent ? r.last : r.title;
              const source = isAgent ? 'agents' : r.source;
              const isActive = idx === safeActiveIndex;

              return (
                <div
                  key={id}
                  data-index={idx}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => handleSelect(id)}
                  onMouseEnter={() => setActiveIndex(idx)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '9px 12px',
                    borderRadius: 10,
                    cursor: 'pointer',
                    background: isActive ? 'var(--z-accent-soft)' : 'transparent',
                    border: 'none',
                    boxShadow: isActive ? '0 1px 3px var(--z-sh-4)' : 'none',
                    transition: 'background 0.12s ease',
                  }}
                >
                  <BrandIcon source={source} size={16} />
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: isActive ? 'var(--z-accent-ink)' : 'var(--z-muted-2)',
                      flex: '0 0 auto',
                    }}
                  >
                    {key}
                  </span>
                  <span
                    style={{
                      flex: '1 1 auto',
                      minWidth: 0,
                      fontSize: 13,
                      color: isActive ? 'var(--z-text)' : 'var(--z-text-3)',
                      fontWeight: isActive ? 600 : 400,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {title}
                  </span>
                  {isActive && (
                    <kbd
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '1.5px 6px',
                        borderRadius: 4,
                        background: 'var(--z-surface)',
                        color: 'var(--z-accent-ink)',
                        border: 'none',
                        flexShrink: 0,
                      }}
                    >
                      ↵
                    </kbd>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Modal Footer with Keyboard Navigation Hints */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '9px 16px',
            borderTop: 'none',
            background: 'var(--z-surface-subtle)',
            fontSize: 11.5,
            color: 'var(--z-dim)',
            userSelect: 'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>
              <kbd style={kbdStyle}>↑↓</kbd> Navegar
            </span>
            <span>·</span>
            <span>
              <kbd style={kbdStyle}>↵</kbd> Seleccionar
            </span>
            <span>·</span>
            <span>
              <kbd style={kbdStyle}>Esc</kbd> Cerrar
            </span>
          </div>
          {results.length > 0 && (
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--z-muted)',
              }}
            >
              {safeActiveIndex + 1} / {results.length}
            </span>
          )}
        </div>
      </div>
    </Modal>
  );
};
